import React, { useMemo, useState } from 'react'
import {
  ArrowsDownUpIcon,
  PencilSimpleIcon,
  UsersThreeIcon,
} from '@phosphor-icons/react'
import { AllGuildTribeHeader, AllGuildTypes, Guild } from '../../types/Guilds'

type SortColumn = 'role' | 'type'

const GUILD_TYPE_LABELS: Record<string, string> = Object.keys(
  AllGuildTypes,
).reduce((acc, key) => {
  acc[AllGuildTypes[key]] = AllGuildTribeHeader[key]
  return acc
}, {} as Record<string, string>)

const getGuildTypeLabel = (type: string) => GUILD_TYPE_LABELS[type] ?? type

interface Props {
  guilds: Guild[]
  onRowClick: (guild: Guild) => void
  onEdit: (guild: Guild) => void
}

const GuildsTable = ({ guilds, onRowClick, onEdit }: Props) => {
  const [sortColumn, setSortColumn] = useState<SortColumn>('role')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')

  const sortedGuilds = useMemo(() => {
    return [...guilds].sort((a, b) => {
      const comparison =
        sortColumn === 'type'
          ? getGuildTypeLabel(a.type).localeCompare(getGuildTypeLabel(b.type))
          : a.role.localeCompare(b.role)
      return sortOrder === 'asc' ? comparison : -comparison
    })
  }, [guilds, sortColumn, sortOrder])

  const handleSort = (column: SortColumn) => {
    if (column === sortColumn) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortOrder('asc')
    }
  }

  const sortableHeader = (label: string, column: SortColumn, width: string) => (
    <th className="manage-guilds__table-th" style={{ width }}>
      <button
        type="button"
        className="manage-guilds__sort-btn"
        onClick={() => handleSort(column)}
      >
        {label}
        <ArrowsDownUpIcon size={14} weight="regular" color="#717182" />
      </button>
    </th>
  )

  if (guilds.length === 0) {
    return (
      <div className="manage-guilds__empty">
        <div className="manage-guilds__empty-icon">
          <UsersThreeIcon size={48} weight="regular" color="#717182" />
        </div>
        <p className="manage-guilds__empty-label">
          No guilds found. Try adjusting your search.
        </p>
      </div>
    )
  }

  return (
    <div className="manage-guilds__table-card">
      <table className="manage-guilds__table">
        <thead className="manage-guilds__table-head">
          <tr>
            {sortableHeader('Guild', 'role', '55%')}
            {sortableHeader('Type', 'type', '45%')}
            <th className="manage-guilds__table-th manage-guilds__table-th--actions" />
          </tr>
        </thead>
        <tbody>
          {sortedGuilds.map(guild => (
            <tr
              key={guild.id}
              className="manage-guilds__table-row"
              onClick={() => onRowClick(guild)}
            >
              <td className="manage-guilds__table-td">{guild.role}</td>
              <td className="manage-guilds__table-td">
                {getGuildTypeLabel(guild.type)}
              </td>
              <td className="manage-guilds__table-td manage-guilds__table-td--actions">
                <button
                  type="button"
                  className="manage-guilds__edit-btn"
                  aria-label={`Edit ${guild.role}`}
                  title="Edit"
                  onClick={e => {
                    e.stopPropagation()
                    onEdit(guild)
                  }}
                >
                  <PencilSimpleIcon size={18} weight="regular" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default GuildsTable
